// diceware.js
// CRA想定: public/ に diceware-ja.csv を置く（例: public/diceware-ja.csv）
/* eslint-env es2020 */

/** ---- CSVロード（キャッシュ付き） ---- */
let _csvTextPromise = null;

function loadDicewareCsvText(path = "/diceware-ja.csv") {
  if (_csvTextPromise) return _csvTextPromise;

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;

  _csvTextPromise = fetch(url).then((r) => {
    if (!r.ok) throw new Error(`Failed to load CSV (${r.status}): ${url}`);
    return r.text();
  });

  return _csvTextPromise;
}

/** ---- 乱数（WebCrypto, unbiased） ---- */
function cryptoRandomInt(max) {
  if (!Number.isInteger(max) || max <= 0) throw new Error("max must be a positive integer");
  const c = globalThis.crypto;
  if (!c?.getRandomValues) throw new Error("crypto.getRandomValues is not available");

  const range = 0x100000000; // 2^32
  const limit = Math.floor(range / max) * max;

  const buf = new Uint32Array(1);
  while (true) {
    c.getRandomValues(buf);
    const x = buf[0];
    if (x < limit) return x % max;
  }
}

/** ---- CSVパース（ダブルクォート対応） ---- */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ",") {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/** ---- key("11111") -> index(0..7775) ---- */
function keyToIndex(key) {
  // 6進数（各桁 1..6 を 0..5 にして詰める）
  let idx = 0;
  for (let i = 0; i < 5; i++) {
    const d = parseInt(key[i], 10); // '1'..'6' => 1..6
    idx = idx * 6 + (d - 1);
  }
  return idx;
}

const EXPECTED = 6 ** 5; // 7776

function assertNoWordDuplicatesAllColumns(entries) {
  // 欠番＆空欄チェック + 全列重複チェック
  // 漢字は重複が多くて使い物にならない。
  const seenKana = new Map();
  const seenRomaji = new Map();

  for (let i = 0; i < EXPECTED; i++) {
    const e = entries[i];
    if (!e) throw new Error(`Wordlist incomplete: missing entry at index ${i}`);

    const { kana, romaji } = e;

    // 空欄は即エラー（列が欠けると運用上の想定が崩れるため）
    if (!kana) throw new Error(`Missing "kana" at index ${i}`);
    if (!romaji) throw new Error(`Missing "romaji" at index ${i}`);

    const prevKana = seenKana.get(kana);
    if (prevKana !== undefined) {
      throw new Error(`Duplicate kana: "${kana}" (indexes ${prevKana} and ${i})`);
    }
    seenKana.set(kana, i);

    const prevRomaji = seenRomaji.get(romaji);
    if (prevRomaji !== undefined) {
      throw new Error(`Duplicate romaji: "${romaji}" (indexes ${prevRomaji} and ${i})`);
    }
    seenRomaji.set(romaji, i);
  }
}

/**
 * CSV例: 11111,愛育,あいいく,aiiku
 * -> entries[0..7775] に格納（key順に依存しない）
 * - key重複: 即エラー
 * - 欠番: 即エラー
 * - 単語重複(kana/romaji 全列): 即エラー
 */
function buildDicewareJaEntries(csvText) {
  const entries = new Array(EXPECTED);
  const seenKey = new Set(); // key重複検出

  const lines = csvText.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const cols = parseCsvLine(line);
    const key = (cols[0] || "").trim();
    if (!/^[1-6]{5}$/.test(key)) continue;

    if (seenKey.has(key)) {
      throw new Error(`Duplicate key in wordlist: ${key}`);
    }
    seenKey.add(key);

    const idx = keyToIndex(key);

    // ここで entries[idx] が既に埋まっていたら、それは key重複と同義（保険）
    if (entries[idx]) {
      throw new Error(`Duplicate assignment at index ${idx} (key=${key})`);
    }

    entries[idx] = {
      kana: (cols[2] || "").trim(),
      romaji: (cols[3] || "").trim(),
    };
  }

  if (seenKey.size !== EXPECTED) {
    throw new Error(`Wordlist incomplete: expected ${EXPECTED} keys, got ${seenKey.size}`);
  }

  // 欠番＆全列重複＆空欄チェック
  assertNoWordDuplicatesAllColumns(entries);

  return entries;
}

/** 例外メッセージ用：index(0..7775) -> key("11111".."66666") */
function indexToKey(index) {
  let x = index;
  const digits = new Array(5);
  for (let i = 4; i >= 0; i--) {
    digits[i] = (x % 6) + 1; // 1..6
    x = Math.floor(x / 6);
  }
  return digits.join("");
}

/** ---- entries キャッシュ（CSV→entries は一度だけ） ---- */
let _entriesPromise = null;

async function getDicewareEntries(csvPath = "/diceware-ja.csv") {
  if (_entriesPromise) return _entriesPromise;

  _entriesPromise = loadDicewareCsvText(csvPath).then((csv) => {
    const entries = buildDicewareJaEntries(csv);
    // ここで length も念のため
    if (entries.length !== EXPECTED) {
      throw new Error(`Internal error: entries length ${entries.length} != ${EXPECTED}`);
    }
    return entries;
  });

  return _entriesPromise;
}

/**
 * 非同期Diceware生成（配列7776個版）
 * - words: デフォルト7
 * - separator: デフォルト全角スペース
 * - column: "kana" | "romaji"（デフォルト "kana"）
 * - csvPath: public/ 配下のパス（デフォルト "/diceware-ja.csv"）
 */
async function generateDicewarePassphraseAsync(opts = {}) {
  const words = opts.words ?? 7;
  const separator = opts.separator ?? "\u3000"; // 全角スペース
  const column = opts.column ?? "kana";
  const csvPath = opts.csvPath ?? "/diceware-ja.csv";

  if (!["kana", "romaji"].includes(column)) {
    throw new Error(`Invalid column: ${column}`);
  }

  const entries = await getDicewareEntries(csvPath);

  const out = [];
  for (let i = 0; i < words; i++) {
    const idx = cryptoRandomInt(EXPECTED); // 0..7775 (unbiased)
    const w = entries[idx]?.[column];
    if (!w) {
      // entriesが完全なら通常起きないが、列が空だった場合はエラーにする
      throw new Error(`Missing "${column}" at index ${idx} (key=${indexToKey(idx)})`);
    }
    out.push(w);
  }

  return out.join(separator);
}

/** ---- 使い方例（React内） ----
import { generateDicewarePassphraseAsync } from "./dicewareJaAsyncArray";

const pass = await generateDicewarePassphraseAsync({
  words: 7,
  column: "kana",
  separator: "　",
  csvPath: "/diceware-ja.csv",
});
console.log(pass);
*/

export {
  loadDicewareCsvText,
  buildDicewareJaEntries,
  generateDicewarePassphraseAsync,
};
